/**
 * A primeira tela: quanto tempo já faz, como cada um está hoje, e o que
 * aconteceu de bom por último.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  COLECOES,
  comecoCurto,
  disseHoje,
  humoresMaisRecentes,
  inicioDaCasa,
  ligado,
  nomeDaCasa,
  outrosDaCasa,
  porExtenso,
  traduzir,
  usarAjustes,
  usarColecao,
  usarEstadoDoArmazem,
  usarSessao,
  type DocDeHumor,
  type NomeDoIcone,
} from '@nosso-sempre/shared';
import { Esqueleto } from '../componentes/Pecas';
import { ArquivoVivo } from '../componentes/ArquivoVivo';
import { Ancora, BarraDePulo } from '../componentes/BarraDePulo';
import { BarraDeXp } from '../componentes/BarraDeXp';
import { CartaoAssistirJunto } from '../componentes/CartaoAssistirJunto';
import { CartaoDeSorteio } from '../componentes/CartaoDeSorteio';
import { CartaoDoJardim } from '../componentes/CartaoDoJardim';
import { CartaoPortal } from '../componentes/CartaoPortal';
import { ContadorJuntos } from '../componentes/ContadorJuntos';
import { EscolhaDeHumor } from '../componentes/EscolhaDeHumor';
import { EstatisticasDeMusica } from '../componentes/EstatisticasDeMusica';
import { Icone } from '../componentes/Icone';
import { Jukebox } from '../componentes/Jukebox';
import { KovuFlutuante } from '../componentes/KovuFlutuante';
import { PreviaDaAgenda } from '../componentes/PreviaDaAgenda';
import { PreviaDaGaleria } from '../componentes/PreviaDaGaleria';
import { PreviaDeNesteDia } from '../componentes/PreviaDeNesteDia';
import { PreviaDeVemAi } from '../componentes/PreviaDeVemAi';
import { PreviaDasCartas } from '../componentes/PreviaDasCartas';
import {
  PreviaDoAnime,
  PreviaDoCinema,
  PreviaDoQueEstamosVendo,
} from '../componentes/PreviaDoCinema';
import { PreviaDoPote } from '../componentes/PreviaDoPote';
import { PreviaDosDesejos } from '../componentes/PreviaDosDesejos';
import { PreviaDosLivros } from '../componentes/PreviaDosLivros';
import logo from '../imagens/logo.webp';
import './painel.css';

/** Um cartão do "acesso rápido", com o `hue` e a legenda do atalho do Flutter. */
interface Atalho {
  para: string;
  nome: string;
  legenda: string;
  icone: NomeDoIcone;
  cor: string;
  /** A chave de recurso que abre ou fecha o caminho, o `_routeFeature` de lá. */
  recurso: string;
  /** Só o líder enxerga: a credencial do banco é de uma pessoa só. */
  soDono?: boolean;
}

/**
 * A tabela do `_buildQuickActions`, na ordem de lá, com os que já têm tela no
 * site.
 */
const ATALHOS: Atalho[] = [
  {
    para: '/galeria',
    nome: 'Galeria',
    legenda: 'Memórias',
    icone: 'fotos',
    cor: 'var(--rosa-quartzo)',
    recurso: 'gallery',
  },
  {
    para: '/sanctuary',
    nome: 'Refúgio',
    legenda: 'Conversa',
    icone: 'balao',
    cor: 'var(--aurora-rosa)',
    recurso: 'sanctuary',
  },
  {
    para: '/receitas',
    nome: 'Receitas',
    legenda: 'Cozinha',
    icone: 'comida',
    cor: 'var(--aurora-ouro)',
    recurso: 'recipes',
  },
  {
    para: '/cardapio',
    nome: 'Cardápio',
    legenda: 'A semana',
    icone: 'semana',
    cor: 'var(--aurora-agua)',
    recurso: 'menu',
  },
  {
    para: '/compras',
    nome: 'Compras',
    legenda: 'No mercado',
    icone: 'carrinho',
    cor: 'var(--rosa-quartzo)',
    recurso: 'shopping',
  },
  {
    para: '/despensa',
    nome: 'Despensa',
    legenda: 'Em casa',
    icone: 'geladeira',
    cor: 'var(--aurora-ouro)',
    recurso: 'despensa',
  },
  {
    para: '/canvas',
    nome: 'Quadro',
    legenda: 'Desenhar',
    icone: 'pincel',
    cor: 'var(--lavanda)',
    recurso: 'canvas',
  },
  {
    para: '/kovu',
    nome: 'Kovu',
    legenda: 'Seu gato',
    icone: 'kovu',
    cor: 'var(--aurora-ouro)',
    recurso: 'kovu',
  },
  {
    para: '/calendario',
    nome: 'Calendário',
    legenda: 'Datas',
    icone: 'calendario',
    cor: 'var(--ambar)',
    recurso: 'calendar',
  },
  {
    para: '/alexa',
    nome: 'Listas',
    legenda: 'Alexa',
    icone: 'lista-certa',
    cor: 'var(--aurora-agua)',
    recurso: 'alexa',
  },
  {
    para: '/lembrancas',
    nome: 'Pote de estrelas',
    legenda: 'Gratidão',
    icone: 'estrela',
    cor: 'var(--lavanda-viva)',
    recurso: 'starlightJar',
  },
  {
    para: '/desejos',
    nome: 'Lista de sonhos',
    legenda: 'Sonhos',
    icone: 'mala',
    cor: 'var(--aurora-agua)',
    recurso: 'bucketList',
  },
  {
    para: '/financas',
    nome: 'Finanças',
    legenda: 'Sicredi',
    icone: 'banco',
    cor: 'var(--aurora-agua)',
    recurso: 'financas',
    soDono: true,
  },
  {
    para: '/letterbox',
    nome: 'Cartas',
    legenda: 'Bilhetes',
    icone: 'carta',
    cor: 'var(--ouro)',
    recurso: 'letterbox',
  },
];

export function Painel() {
  const { casa, quem } = usarSessao();
  const ajustes = usarAjustes();
  const { pronto } = usarEstadoDoArmazem();

  const humores = usarColecao<DocDeHumor>(COLECOES.humores);

  const inicio = useMemo(() => inicioDaCasa(ajustes), [ajustes]);

  /** O humor mais recente de cada pessoa. */
  const humorDe = useMemo(() => humoresMaisRecentes(humores), [humores]);

  /**
   * O que o Kovu tem para contar de quem mora com você: o `partnerName` mais o
   * `moodEmoji` da fala de humor dele. Quem ainda não disse nada fica de fora —
   * "Fulana está  hoje!" não é frase.
   */
  const humoresDaCasa = useMemo(
    () =>
      outrosDaCasa(casa, quem)
        .map((p) => ({
          nome: p.nome,
          emoji: humorDe.get(p.slug.toLowerCase())?.doc.moodEmoji ?? '',
        }))
        .filter((h) => h.emoji.length > 0),
    [casa, quem, humorDe],
  );

  const euSou = casa?.pessoas.find((p) => p.slug === quem);

  /**
   * Os atalhos do `_buildQuickActions` do painel do Flutter: nome em cima,
   * legenda embaixo, e o `hue` de cada um. A ordem e as palavras são as de lá.
   *
   * A lista só traz os que já têm tela aqui; os que faltam entram junto com a
   * tela deles, porque um atalho para um caminho que não existe volta para o
   * painel e vira um beco.
   */
  const atalhos = ATALHOS.filter((a) => ligado(ajustes, a.recurso)).filter(
    (a) => !a.soDono || casa?.lider === quem,
  );

  return (
    <>
      <Cabecalho inicio={inicio} eu={euSou?.nome} casa={nomeDaCasa(casa)} />

      {/* A barra de nível vem logo depois do cabeçalho, que é onde ela cai lá
          (dashboard_screen.dart:339). Traz o próprio respiro de 8, o mesmo
          `vertical: 8` do `Padding` de lá, e por isso fica fora do `.bloco`. */}
      {ligado(ajustes, 'xp') ? <BarraDeXp /> : null}

      <ContadorJuntos inicio={inicio} />

      <section aria-labelledby="hoje" className="bloco">
        <h2 id="hoje">{traduzir('Como estamos hoje')}</h2>
        {!pronto ? (
          <Esqueleto quantos={2} altura={56} />
        ) : (
          <ul className="gente">
            {(casa?.pessoas ?? []).map((p) => {
              const h = humorDe.get(p.slug.toLowerCase());
              return (
                <li key={p.slug} className="cartao pessoa">
                  <span className="ponto" style={{ background: p.cor }} aria-hidden="true" />
                  <span className="nome">{p.nome}</span>
                  {h ? (
                    <span className="humor">
                      {/* Sobre 5, e não sobre 10: o `MoodPicker` do Flutter dá
                          cinco escolhas, de 1 a 5, e é ele quem escreve o
                          `moodScore` que está sendo lido aqui. */}
                      <span aria-hidden="true">{h.doc.moodEmoji ?? ''}</span>
                      <span className="discreto">{h.doc.moodScore ?? '?'}/5</span>
                    </span>
                  ) : (
                    <span className="discreto">{traduzir('ainda não disse')}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/* A pergunta do `MoodPicker`, e o `hasSubmittedToday` decidindo se ela
            aparece: quem já disse hoje não é perguntado de novo. Depois da
            meia-noite ela volta, como lá. */}
        {pronto &&
        ligado(ajustes, 'heartbeat') &&
        !disseHoje(humorDe.get((quem ?? '').toLowerCase())?.quando) ? (
          <EscolhaDeHumor />
        ) : null}
      </section>

      {/* A barra de atalhos vem logo antes da primeira âncora, como lá
          (dashboard_screen.dart:375-380). */}
      <BarraDePulo />

      {/* "Neste dia" abre o conteúdo do painel, logo depois do cabeçalho e das
          ações rápidas, que é onde ele cai lá (dashboard_screen.dart:392). É
          uma das poucas fatias sem `so:`: não desliga. Quando não há lembrança
          de outro ano ele mesmo se apaga. */}
      <Ancora id="coming-up" />
      <PreviaDeNesteDia />

      {/* "Vem aí" vem logo em seguida, que é o lugar dele lá
          (dashboard_screen.dart:400), e também não desliga. Não é a
          `PreviaDaAgenda` mais abaixo: aquela é o `CalendarPreview`, de trinta
          dias e desligável; esta é a contagem regressiva de sessenta. */}
      <div className="bloco">
        <PreviaDeVemAi />
      </div>

      {/* A prateleira vem antes da agenda, que é onde o `BooksPreview` cai na
          ordem das fatias de lá (dashboard_screen.dart:471). */}
      {ligado(ajustes, 'books') ? (
        <div className="bloco">
          <PreviaDosLivros />
        </div>
      ) : null}

      {/* O álbum entra entre a prateleira e a agenda, que é onde o
          `GalleryPreview` cai na ordem das fatias (dashboard_screen.dart:490). */}
      {ligado(ajustes, 'gallery') ? (
        <div className="bloco">
          <PreviaDaGaleria />
        </div>
      ) : null}

      {/* A prévia da agenda vem antes da linha do tempo, como no painel do
          Flutter, e some junto com o recurso quando a casa o desliga. */}
      {ligado(ajustes, 'calendar') ? (
        <div className="bloco">
          <PreviaDaAgenda />
        </div>
      ) : null}

      {/* O "ARQUIVO VIVO", que é o `TimelineView` no lugar dele
          (dashboard_screen.dart:499), também sem `so:`. Traz a própria folga de
          cima e desenha o próprio vazio quando não há marco nenhum — com a
          porta de guardar o primeiro dentro dele —, então fica fora do
          `.bloco`, que deixaria 24 de margem em volta da faixa. */}
      <Ancora id="timeline" />
      <ArquivoVivo />

      {/* O pote de estrelas logo depois da linha do tempo, no lugar em que a
          âncora "moments" do Flutter o põe (dashboard_screen.dart:514-526). */}
      {ligado(ajustes, 'starlightJar') ? (
        <>
          <Ancora id="moments" />
          <div className="bloco">
            <PreviaDoPote />
          </div>
        </>
      ) : null}

      {/* A caixa de cartas vem antes da vitrola, como o `LetterboxView` na
          ordem das fatias (dashboard_screen.dart:407). É a única fatia da lista
          de lá sem `so:`: a caixa de cartas não desliga. */}
      <div className="bloco">
        <PreviaDasCartas />
      </div>

      {/* Entre o que ficou e o acesso rápido, que é onde eles caem na ordem das
          fatias do painel do Flutter: vitrola, estatísticas, roleta e só então
          o assistir junto. O respiro de lá é 32; aqui é o `.bloco` de 24, o
          mesmo de todos os vizinhos desta pilha. */}
      {ligado(ajustes, 'jukebox') ? (
        <div className="bloco">
          <Jukebox />
        </div>
      ) : null}

      {ligado(ajustes, 'jukebox') ? (
        <div className="bloco">
          <EstatisticasDeMusica />
        </div>
      ) : null}

      {ligado(ajustes, 'dateRandomizer') ? (
        <div className="bloco">
          <CartaoDeSorteio />
        </div>
      ) : null}

      {/* As duas prateleiras de cinema, com o assistir junto no meio: é a ordem
          exata das fatias de lá (`CurrentlyWatchingPreview` 1080, `WatchPartyCard`
          1100, `CinemaPreview` 1100). As duas atendem à mesma chave. */}
      {ligado(ajustes, 'cinema') ? (
        <>
          <Ancora id="watching" />
          <div className="bloco">
            <PreviaDoQueEstamosVendo />
          </div>
        </>
      ) : null}

      {ligado(ajustes, 'watchParty') ? <CartaoAssistirJunto /> : null}

      {ligado(ajustes, 'cinema') ? (
        <>
          <Ancora id="shelves" />
          <div className="bloco">
            <PreviaDoCinema />
          </div>
        </>
      ) : null}

      {/* O anime logo depois dos vistos, na própria chave, como lá
          (dashboard_screen.dart:467-473). Nasce desligado: quem não assiste
          anime nunca vê uma prateleira vazia no painel. */}
      {ligado(ajustes, 'anime') ? (
        <div className="bloco">
          <PreviaDoAnime />
        </div>
      ) : null}

      <section aria-labelledby="acesso-rapido">
        <p className="rapido-tarja">
          <span className="rapido-fio curto" aria-hidden="true" />
          <span className="rapido-selo" id="acesso-rapido">
            <Icone nome="grade" tamanho={11} />
            {traduzir('Acesso rápido')}
          </span>
          <span className="rapido-fio" aria-hidden="true" />
        </p>

        <div className="atalhos">
          {atalhos.map((a) => (
            <Link
              key={a.para}
              className="atalho"
              to={a.para}
              style={{ ['--cor-secao' as string]: a.cor }}
            >
              <span className="atalho-fio" aria-hidden="true" />
              <span className="atalho-selo" aria-hidden="true">
                <Icone nome={a.icone} tamanho={18} />
              </span>
              <span className="atalho-nome">{traduzir(a.nome)}</span>
              <span className="atalho-legenda">{traduzir(a.legenda)}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Depois do acesso rápido, que é onde o `DailyBloom` cai na lista de
          fatias do painel do Flutter (dashboard_screen.dart:528). */}
      {ligado(ajustes, 'dailyBloom') ? (
        <div className="bloco">
          <CartaoDoJardim />
        </div>
      ) : null}

      {/* As duas portas, na ordem de lá (dashboard_screen.dart:534-545): a
          academia primeiro, a zona de jogos depois. São a única entrada das duas
          alas — `/academy` e `/play-zone` não aparecem em mais lugar nenhum. */}
      {ligado(ajustes, 'academy') ? (
        <div className="bloco">
          <CartaoPortal
            icone="livro-aberto"
            titulo={traduzir('Nosso Sempre Academy')}
            botao={traduzir('ENTER PORTAL')}
            para="/academy"
            rotulo={traduzir('Entrar na Academia Nosso Sempre')}
          />
        </div>
      ) : null}

      {ligado(ajustes, 'playZone') ? (
        <div className="bloco">
          <CartaoPortal
            icone="controle"
            titulo={traduzir('Zona de Jogos')}
            botao={traduzir('ENTER PLAY ZONE')}
            para="/play-zone"
            rotulo={traduzir('Entrar na Zona de Jogos')}
          />
        </div>
      ) : null}

      {/* A lista de sonhos fecha o painel, como o `BucketListPreview` fecha a
          lista de fatias de lá (dashboard_screen.dart:559). */}
      {ligado(ajustes, 'bucketList') ? (
        <div className="bloco">
          <PreviaDosDesejos />
        </div>
      ) : null}

      {/* O Kovu do `DashboardOverlays`, que lá é o `Positioned(bottom: 24,
          right: 24)`. Vem por último no HTML porque é o que menos interessa a
          quem anda de teclado, e flutua por cima do palco pelo CSS.

          Divergência: lá o gato aparece sempre. Aqui a conversa dele é um
          recurso que a casa pode desligar, e um gato que leva a uma tela
          desligada é um beco — ele acompanha o próprio caminho. */}
      {ligado(ajustes, 'kovu') ? <KovuFlutuante humores={humoresDaCasa} /> : null}
    </>
  );
}

/**
 * O cabeçalho do painel, transcrito do `_buildHeader` de
 * `dashboard_screen.dart` (linhas 589-792): a tarja com a data, o emblema que
 * respira, a saudação à mão, o título com o brilho dourado passando por cima,
 * a data por extenso entre dois pontinhos e o traço com o coração batendo.
 *
 * É a assinatura do app — a única tela que o Flutter trata como capa — e
 * estava faltando inteira aqui.
 */
function Cabecalho({ inicio, eu, casa }: { inicio: Date; eu?: string; casa: string }) {
  const hora = new Date().getHours();
  const parte =
    hora < 12 ? traduzir('bom dia') : hora < 18 ? traduzir('boa tarde') : traduzir('boa noite');
  const saudacao = eu ? `${parte}, ${eu}` : traduzir('bem-vindos ao nosso lar');

  return (
    <header className="capa">
      <p className="tarja">
        <span className="tarja-ponto" aria-hidden="true" />
        {traduzir('DESDE  {v}  ·  {v2}', {
          v: comecoCurto(inicio).toUpperCase(),
          v2: casa.toUpperCase(),
        })}
      </p>

      <div className="emblema" aria-hidden="true">
        <span className="emblema-aura" />
        <span className="emblema-varredura" />
        <span className="emblema-arte">
          <span className="emblema-brilho rosa" />
          <span className="emblema-brilho ouro" />
          <img src={logo} alt="" />
        </span>
      </div>

      <p className="saudacao">{saudacao}</p>

      <h1 className="capa-titulo">{traduzir('Nosso Sempre')}</h1>

      <p className="capa-desde">
        <span className="capa-conta" aria-hidden="true" />
        {traduzir('desde {quando}', { quando: porExtenso(inicio) })}
        <span className="capa-conta" aria-hidden="true" />
      </p>

      <p className="capa-traco" aria-hidden="true">
        <span className="capa-fio esquerdo" />
        <span className="capa-semente" />
        <span className="capa-coracao">
          <Icone nome="coracao" tamanho={14} preenchido />
        </span>
        <span className="capa-semente" />
        <span className="capa-fio direito" />
      </p>
    </header>
  );
}
